<template>
  <div
    class="s-company-card-skeleton"
    :class="{
      's-company-card-skeleton--logo-emphasized': logoEmphasized,
      's-company-card-skeleton--mobile-forced': mobileForced,
    }"
  >
    <template v-if="mobileForced">
      <PSkeleton
        class="s-company-card-skeleton__logo"
        height="40px"
        width="40px"
      />
      <PSkeleton
        class="s-company-card-skeleton__company-name"
        height="20px"
        width="200px"
      />
    </template>
    <template v-else>
      <PSkeleton
        class="s-company-card-skeleton__logo s-company-card-skeleton__logo--mobile"
        :height="logoEmphasized ? '96px' : '40px'"
        :width="logoEmphasized ? '96px' : '40px'"
      />
      <PSkeleton
        class="s-company-card-skeleton__logo s-company-card-skeleton__logo--desktop"
        height="96px"
        width="96px"
      />
      <PSkeleton
        class="s-company-card-skeleton__company-name s-company-card-skeleton__company-name--mobile"
        height="20px"
        width="200px"
      />
      <PSkeleton
        class="s-company-card-skeleton__company-name s-company-card-skeleton__company-name--desktop"
        :height="logoEmphasized ? '40px' : '28px'"
        :width="logoEmphasized ? '320px' : '240px'"
      />
    </template>
    <div class="s-company-card-skeleton__badges">
      <PSkeleton height="24px" width="65px" />
      <PSkeleton height="24px" width="110px" />
      <PSkeleton height="24px" width="110px" />
    </div>
    <PSkeleton
      class="s-company-card-skeleton__review-score"
      height="18px"
      width="240px"
    />
    <div class="s-company-card-skeleton__content">
      <PSkeleton height="18px" style="width: 95%" />
      <PSkeleton height="18px" style="width: 91%" />
      <PSkeleton height="18px" style="width: 97%" />
      <PSkeleton
        v-if="hasShowMore"
        height="28px"
        style="margin-top: 8px"
        width="106px"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import PSkeleton from "@/atoms/PSkeleton.vue";

withDefaults(
  defineProps<{
    hasShowMore?: boolean;
    logoEmphasized?: boolean;
    mobileForced?: boolean;
  }>(),
  {
    logoEmphasized: false,
    hasShowMore: false,
    mobileForced: false,
  },
);
</script>

<style lang="postcss">
.s-company-card-skeleton {
  @apply grid items-center;
  grid-template-areas:
    "logo company-name"
    "badges badges"
    "review-score review-score"
    "card-content card-content";
  grid-template-columns: max-content 1fr;

  &--logo-emphasized {
    grid-template-areas:
      "logo logo"
      "company-name company-name"
      "badges badges"
      "review-score review-score"
      "card-content card-content";
  }

  &__logo {
    @apply mb-2 mr-2 rounded-lg;
    grid-area: logo;

    &--mobile {
      @screen 600 {
        @apply hidden;
      }
    }
    &--desktop {
      @apply hidden;
      @screen 600 {
        @apply block;
      }
    }
  }
  &__company-name {
    @apply mb-2;
    grid-area: company-name;

    &--mobile {
      @screen 600 {
        @apply hidden;
      }
    }
    &--desktop {
      @apply hidden;
      @screen 600 {
        @apply block;
      }
    }
  }
  &__badges {
    @apply mb-2 flex flex-wrap gap-2;
    grid-area: badges;
  }
  &__review-score {
    @apply mb-2;
    grid-area: review-score;
  }
  &__content {
    @apply mt-1 flex flex-col gap-1;
    grid-area: card-content;
  }

  &--mobile-forced {
    @apply items-start p-4;

    .s-company-card-skeleton__company-name {
      @apply h-auto;
    }
  }
}

@screen 600 {
  .s-company-card-skeleton {
    @apply gap-x-3;
    grid-template-areas:
      "logo company-name"
      "logo badges"
      "logo review-score"
      "logo no-reviews"
      "logo location"
      "logo price-preview"
      "logo match-status"
      "card-content card-content";

    &__company-name {
      @apply h-10;
    }

    &--mobile-forced {
      grid-template-areas:
        "logo company-name"
        "badges badges"
        "review-score review-score"
        "card-content card-content";
      grid-template-columns: max-content 1fr;

      .s-company-card-skeleton__company-name {
        @apply h-auto;
      }
    }
  }
}
</style>
